import {Palette} from 'hubbl-shared';
import {memo, useCallback, useEffect, useState} from 'react';
import {Menu, Sidebar as ProSidebar, sidebarClasses} from 'react-pro-sidebar';
import sidebarBackgroundImage from '../../assets/images/sidebarBackgroundImage.png';
import {useResponsive, useTouchGestures} from '../../hooks';
import {SidebarFooter} from './components/menu-footer/SidebarFooter';
import {SidebarMenuHeader} from './components/menu-header/SidebarMenuHeader';
import {EmployeeMenuItemsContainer} from './components/employee-menu-items-container/EmployeeMenuItemsContainer';
import {MobileMenuToggle} from './components/mobile-menu-toggle/MobileMenuToggle';
import {MobileOverlay} from './components/mobile-overlay/MobileOverlay';

export const EmployeeSidebar = memo(() => {
  const {isMobile} = useResponsive();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Handle mobile sidebar state
  useEffect(() => {
    if (!isMobile) {
      setIsMobileOpen(false);
    }
  }, [isMobile]);

  // Close mobile sidebar when clicking outside
  const handleOverlayClick = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  // Toggle mobile sidebar
  const handleMobileToggle = useCallback(() => {
    setIsMobileOpen(prev => !prev);
  }, []);

  // Handle header click (for desktop collapse)
  const onClickHeader = useCallback(() => {
    if (!isMobile) {
      setIsCollapsed(prev => !prev);
    }
  }, [isMobile]);

  // Close mobile sidebar when menu items are clicked
  const handleMenuClick = useCallback(() => {
    if (isMobile) {
      setIsMobileOpen(false);
    }
  }, [isMobile]);

  // Touch gestures for mobile
  useTouchGestures({
    onSwipeRight: () => {
      if (isMobile && !isMobileOpen) {
        setIsMobileOpen(true);
      }
    },
    onSwipeLeft: () => {
      if (isMobile && isMobileOpen) {
        setIsMobileOpen(false);
      }
    },
    enabled: isMobile,
    threshold: 80,
  });

  return (
    <>
      <MobileMenuToggle onClick={handleMobileToggle} isOpen={isMobileOpen} />

      <MobileOverlay isVisible={isMobileOpen} onClick={handleOverlayClick} />

      <ProSidebar
        style={{
          backgroundImage: isMobile ? 'none' : `url(${sidebarBackgroundImage})`,
          backgroundSize: 'cover',
          boxShadow: isMobile ? 'none' : `0px 4px 24px 0px rgb(0 0 0 / 8%)`,
        }}
        rootStyles={{
          [`.${sidebarClasses.container}`]: {
            position: isMobile ? 'fixed' : 'relative',
            zIndex: isMobile ? 999 : 'auto',
            height: '100vh',
            width: isMobile ? '280px' : 'auto',
            transform:
              isMobile && !isMobileOpen ? 'translateX(-100%)' : 'translateX(0)',
            transition: isMobile ? 'transform 0.3s ease' : 'none',
            overflowY: isMobile ? 'auto' : 'visible',
            WebkitOverflowScrolling: isMobile ? 'touch' : 'auto',
            backgroundColor: isMobile ? Palette.white : 'transparent',
            boxShadow: isMobile ? '4px 0 24px rgba(0, 0, 0, 0.15)' : 'none',
            borderRight: isMobile
              ? `1px solid ${Palette.almostBlack750 || '#e5e7eb'}`
              : 'none',
            /* Hide scrollbar for mobile */
            scrollbarWidth: isMobile ? 'none' : 'auto',
            msOverflowStyle: isMobile ? 'none' : 'auto',
            '&::-webkit-scrollbar': {
              display: isMobile ? 'none' : 'block',
            },
          },
        }}
        backgroundColor="transparent"
        collapsed={!isMobile && isCollapsed}>
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            backgroundColor: isMobile ? Palette.white : 'transparent',
            padding: isMobile ? '0' : '0',
          }}>
          <Menu
            style={{
              flex: 1,
              paddingTop: isMobile ? '4rem' : '0',
              backgroundColor: isMobile ? Palette.white : 'transparent',
              position: 'relative',
            }}
            menuItemStyles={{
              button: ({level, active, disabled}) => {
                // only apply styles on first level elements of the tree
                if (level === 0)
                  return {
                    color: isMobile ? Palette.almostBlack : Palette.almostBlack,
                    backgroundColor: active
                      ? isMobile
                        ? Palette.primarySubtleGreen || '#f0f9ff'
                        : Palette.whiteTransparent40
                      : undefined,
                    '&:hover': {
                      backgroundColor: isMobile
                        ? Palette.primarySubtleGreen || '#f0f9ff'
                        : undefined,
                    },
                  };
              },
            }}>
            <SidebarMenuHeader
              onClick={onClickHeader}
              isCollapsed={!isMobile && isCollapsed}
              isMobile={isMobile}
            />

            <EmployeeMenuItemsContainer
              isCollapsed={!isMobile && isCollapsed}
              onMenuClick={handleMenuClick}
            />
          </Menu>
          <SidebarFooter isCollapsed={!isMobile && isCollapsed} />
        </div>
      </ProSidebar>
    </>
  );
});
